import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { GetPetDetailsFromPolicyNumber } from '../../../helpers/petHelpers';
import { ClaimDetailPropTypes } from '../../../propTypes/claimDetails';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import { ClaimSummary } from '../claimSummary/claimSummary';
import { LogicalClaimsDetails } from '../logicalClaims/logicalClaimsDetails';
import { ACTION_BUTTON_TYPE, logicalClaimStatusMap } from './claimStatusMap';
import { ClaimsBenefitCalculationPopup } from '../claimsBenefitCalculationPopup/claimsBenefitCalculationPopup';
import { ReimbursementPopup } from '../../payments/reimbursementPopup/reimbursementPopup';
import { sanitizedAll } from '../../../utils/sanitize';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { EVENT_KEY } from '../../../constants/constants';

export const ClaimDetailItem = ({
  icon,
  includeDescription = true,
  claim,
  petDetails,
  includeCompleteButton = false,
  includeDeleteButton = false
}) => {
  const navigate = useNavigate();
  const [selectedActionComponent, setSelectedActionComponent] = useState(null);
  const [showLogicalDetails, setShowLogicalDetails] = useState(false);
  const [selectedLogicalClaimNumber, setSelectedLogicalClaimNumber] = useState(null);
  const {
    PolicyNumber,
    ReceivedDate,
    LogicalClaims,
    ClaimPolicyNumber,
    ClaimDescription,
    Modified,
    FullClaimNumber,
    CustomerClaimId,
    ClaimNumber,
    UploadAdditionalDocs
  } = claim;

  const petDetail = GetPetDetailsFromPolicyNumber({
    petDetails,
    policyNumber: PolicyNumber || ClaimPolicyNumber
  });

  const { Species, FullPolicyNumber, PolicyDetails, Name, PetProfilePicture } = petDetail;
  const { PetGender, ExcessType } = PolicyDetails;
  const sanitizedClaimDescription = sanitizedAll(ClaimDescription);

  const handleActionButtonClick = (type, logicalClaimNumber) => {
    setSelectedActionComponent(type);
    setSelectedLogicalClaimNumber(logicalClaimNumber);
  };

  /*
  TODO: This should be moved to UI Library for modals
  */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === EVENT_KEY.ESCAPE) {
        setSelectedActionComponent(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Render action popups
  const renderActionComponent = () => {
    if (selectedActionComponent === ACTION_BUTTON_TYPE.ADD_REIMBURSEMENT_ACCOUNT) {
      return (
        <ReimbursementPopup
          onCancel={() => setSelectedActionComponent(null)}
          activePet={petDetail}
        />
      );
    }
    if (selectedActionComponent === ACTION_BUTTON_TYPE.BENEFIT_CALCULATION) {
      return (
        <ClaimsBenefitCalculationPopup
          onClose={() => setSelectedActionComponent(null)}
          claimNo={ClaimNumber}
          logicalNo={selectedLogicalClaimNumber}
          excessType={ExcessType}
        />
      );
    }
    if (selectedActionComponent === ACTION_BUTTON_TYPE.UPLOAD_DOCUMENTS) {
      navigate(CSPUrlsMap.uploadRequiredDocuments.url, {
        state: {
          claim
        }
      });
      setSelectedActionComponent(null);
      return null;
    }

    return null;
  };

  const renderActionButton = () =>
    LogicalClaims.map((logicalClaim, idx) => {
      const contentByStatus = logicalClaimStatusMap({
        isUploadAdditionalDocs: UploadAdditionalDocs,
        statusCode: logicalClaim.StatusCode,
        logicalClaimStatus: logicalClaim.Status,
        benefitPaid: logicalClaim.BenefitPaid
      });

      const actionButton = contentByStatus?.renderActionButton;
      return actionButton ? (
        <span key={logicalClaim.LogicalClaimNumber || idx}>
          {actionButton({
            onClick: (type) => handleActionButtonClick(type, logicalClaim.LogicalClaimNumber),
            petName: Name
          })}
        </span>
      ) : null;
    });

  return (
    <li className="claimDetailItem">
      {renderActionComponent()}
      {!LogicalClaims || LogicalClaims.length === 0 ? (
        <ClaimSummary
          icon={icon}
          species={Species}
          gender={PetGender}
          name={Name}
          claimNo={FullClaimNumber}
          modified={Modified}
          profilePic={PetProfilePicture}
          fullPolicyNumber={FullPolicyNumber}
          receivedDate={ReceivedDate || Modified}
          includeDescription={includeDescription}
          description={sanitizedClaimDescription}
          includeCompleteButton={includeCompleteButton}
          includeDeleteButton={includeDeleteButton}
          customerClaimId={CustomerClaimId}
        />
      ) : (
        <>
          <ClaimSummary
            icon={icon}
            species={Species}
            gender={PetGender}
            name={Name}
            claimNo={FullClaimNumber}
            modified={Modified}
            profilePic={PetProfilePicture}
            fullPolicyNumber={FullPolicyNumber}
            receivedDate={ReceivedDate || Modified}
            includeDescription={includeDescription}
            description={sanitizedClaimDescription}
            includeCompleteButton={includeCompleteButton}
            includeDeleteButton={includeDeleteButton}
            customerClaimId={CustomerClaimId}
            handleActionButtonClick={handleActionButtonClick}
            isUploadAdditionalDocs={UploadAdditionalDocs}
            renderActionButton={renderActionButton}
          />

          <section className="logicalClaimsHeadingContainer">
            <p>This claim has been divided into the following parts:</p>
          </section>
          <div className="showMoreWithDetailsMobile">
            <button
              type="button"
              className="button text_only"
              onClick={() => setShowLogicalDetails((prev) => !prev)}
            >
              {showLogicalDetails ? 'Hide Details' : 'Show More'}
            </button>
          </div>

          {LogicalClaims.map((logicalClaim, idx) => (
            <React.Fragment key={logicalClaim.LogicalClaimNumber || idx}>
              {showLogicalDetails && (
                <div className="logicalClaimsMobileOnly">
                  <LogicalClaimsDetails
                    logicalClaim={logicalClaim}
                    petName={Name}
                    handleActionButtonClick={handleActionButtonClick}
                    isUploadAdditionalDocs={UploadAdditionalDocs}
                  />
                </div>
              )}
              <div className="logicalClaimsDesktopOnly">
                <LogicalClaimsDetails
                  logicalClaim={logicalClaim}
                  petName={Name}
                  handleActionButtonClick={handleActionButtonClick}
                  isUploadAdditionalDocs={UploadAdditionalDocs}
                />
              </div>
            </React.Fragment>
          ))}
        </>
      )}
    </li>
  );
};

ClaimDetailItem.propTypes = {
  icon: PropTypes.string,
  includeDescription: PropTypes.bool,
  claim: PropTypes.shape(ClaimDetailPropTypes),
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  includeCompleteButton: PropTypes.bool,
  includeDeleteButton: PropTypes.bool
};
